import { useCallback, useEffect, useMemo, useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/supabase/client'
import { useAuth } from '@/lib/auth-context'
import { useStorybookContext } from './useStorybookContext'

type StorybookStatus = 'draft' | 'submitted' | 'approved' | 'published'

interface Storybook {
  id: string
  title: string | null
  status: string | null
  updated_at: string | null
  created_at: string | null
  pdf_url: string | null
  include_illustrations: boolean | null
}

interface Participant {
  id: string
  display_name: string | null
}

interface NicheApp {
  key: string
  title: string
  description: string
}

interface FormState {
  participantIds: string[]
  includeTeacher: boolean
  styleId: string
  title: string
  notes: string
}

interface StatusBadgeProps {
  status: StorybookStatus
}

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
}

const STYLE_OPTIONS = [
  { id: 'luminous-geometry@4.0', label: 'Luminous geometry @4.0' },
  { id: 'soft-watercolors@1.0', label: 'Soft watercolors @1.0' },
  { id: 'storybook-classic@3.0', label: 'Storybook classic @3.0' },
]

const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  published: 'Published',
}

const NICHE_APPS = [
  {
    key: 'pirate',
    title: 'Pirate Day Adventure',
    description:
      'Transform class photos into a swashbuckling adventure complete with treasure maps, scripts, and printable props.',
  },
  {
    key: 'field-trip',
    title: 'Field Trip Recap',
    description: 'Generate a timeline narrative and highlight map for your next community outing or museum visit.',
  },
  {
    key: 'gratitude',
    title: 'Gratitude Wall',
    description: 'Compile sponsor shout-outs and student artwork into a printable thank-you collage.',
  },
]

function StatusBadge({ status }: StatusBadgeProps) {
  const label = STATUS_LABELS[status] ?? status
  const colorMap: Record<StorybookStatus, string> = {
    draft: 'bg-sand text-cocoa',
    submitted: 'bg-blue-100 text-blue-800',
    approved: 'bg-emerald-100 text-emerald-800',
    published: 'bg-brick text-white',
  }

  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${colorMap[status] || 'bg-sand'}`}>
      {label}
    </span>
  )
}

function Modal({ open, onClose, title, children }: ModalProps) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold text-cocoa">{title}</h3>
          </div>
          <button
            type="button"
            className="rounded-full border border-sand bg-shell px-3 py-1 text-sm text-cocoa/70 hover:text-cocoa"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="mt-4 space-y-4 text-sm text-cocoa/80">{children}</div>
      </div>
    </div>
  )
}

export default function StorybooksHome() {
  const navigate = useNavigate()
  const { session } = useAuth()
  const {
    enabled,
    loading,
    error,
    classroom,
    featureEnabled,
    creditsRemaining,
    updatePreferences,
    preferences,
    hasFreeAvailable,
    storybooksAvailable,
    nextStorybookCost,
    storybookCost,
  } = useStorybookContext()
  const teacherId = session?.user?.id ?? null
  const accessToken = session?.access_token ?? null

  const [storybooks, setStorybooks] = useState<Storybook[]>([])
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState<Error | null>(null)
  const [activeModal, setActiveModal] = useState<NicheApp | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [savingPreference, setSavingPreference] = useState(false)
  const [creating, setCreating] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [roster, setRoster] = useState<Participant[]>([])
  const [rosterLoading, setRosterLoading] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>({
    participantIds: [],
    includeTeacher: true,
    styleId: STYLE_OPTIONS[0]?.id ?? '',
    title: '',
    notes: '',
  })

  useEffect(() => {
    if (!enabled || !teacherId) return
    let isMounted = true

    const load = async () => {
      setListLoading(true)
      setListError(null)
      const { data, error: listErr } = await supabase
        .from('story_books')
        .select('id, title, status, updated_at, created_at, pdf_url, include_illustrations')
        .eq('teacher_id', teacherId)
        .order('updated_at', { ascending: false })

      if (!isMounted) return
      if (listErr) {
        setListError(listErr)
      } else {
        setStorybooks(data ?? [])
      }
      setListLoading(false)
    }

    load()
    return () => {
      isMounted = false
    }
  }, [enabled, teacherId])

  const canCreate = useMemo(() => {
    if (!enabled) return false
    if (!featureEnabled) return false
    if (!classroom?.id) return false
    // Can create if free storybook available OR have enough Tower Credits
    return storybooksAvailable > 0
  }, [classroom?.id, storybooksAvailable, enabled, featureEnabled])

  const helperMessage = useMemo(() => {
    if (!enabled) return 'Storybooks are currently turned off.'
    if (!classroom) return 'Add your classroom first to start Storybooks.'
    if (!featureEnabled) return 'Storybooks are disabled for this classroom. Reach out to your Project Tower contact to enable it.'
    if (storybooksAvailable <= 0) {
      return `You need at least ${storybookCost} Tower Credits to create a storybook. Add credits to your wallet to continue.`
    }
    return null
  }, [classroom, storybooksAvailable, storybookCost, enabled, featureEnabled])

  const handleNewStorybook = useCallback(() => {
    if (!canCreate) return
    setShowCreateModal(true)
    setModalError(null)
  }, [canCreate])

  useEffect(() => {
    if (!showCreateModal || !classroom?.id) return
    let active = true
    setRosterLoading(true)
    ;(async () => {
      const { data, error: rosterErr } = await supabase
        .from('participants')
        .select('id, display_name')
        .eq('classroom_id', classroom.id)
        .order('display_name', { ascending: true })

      if (!active) return
      if (!rosterErr && Array.isArray(data)) {
        setRoster(data)
      }
      setRosterLoading(false)
    })()
    return () => {
      active = false
    }
  }, [classroom?.id, showCreateModal])

  const toggleStudent = (id: string) => {
    setForm((prev) => {
      const exists = prev.participantIds.includes(id)
      const nextIds = exists ? prev.participantIds.filter((sid) => sid !== id) : [...prev.participantIds, id]
      return { ...prev, participantIds: nextIds }
    })
  }

  const updateField = (key: keyof FormState, value: string | boolean | string[]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const submitModal = async () => {
    if (!classroom?.id || !teacherId) return
    setCreating(true)
    setModalError(null)
    try {
      const { data: book, error: insertError } = await supabase
        .from('story_books')
        .insert({
          classroom_id: classroom.id,
          teacher_id: teacherId,
          title: form.title || `${classroom.name || 'My'} Storybook`,
          status: 'draft',
          teacher_notes: form.notes || null,
          include_illustrations: true,
          style_registry_id: form.styleId || null,
          student_profiles: form.participantIds?.length
            ? form.participantIds.map((pid: string) => ({ id: pid }))
            : [],
          settings: form.includeTeacher ? { include_teacher: true } : {},
        })
        .select('id, title, status, created_at, updated_at')
        .single()

      if (insertError) throw insertError
      if (!book?.id) throw new Error('No book returned')

      setStorybooks((prev) => {
        const existing = Array.isArray(prev) ? prev : []
        const entry: Storybook = {
          id: book.id,
          title: book.title,
          status: book.status,
          updated_at: book.updated_at,
          created_at: book.created_at,
          include_illustrations: true,
          pdf_url: null,
        }
        return [entry, ...existing]
      })
      setShowCreateModal(false)
      setForm((prev) => ({ ...prev, title: '', notes: '' }))
      navigate(book.id)
    } catch (createError) {
      console.error('[Storybooks] Failed to create storybook', createError)
      setModalError((createError as Error)?.message || 'We could not start a new storybook right now. Please try again later.')
    } finally {
      setCreating(false)
    }
  }

  const handlePreferenceToggle = async (key: string, value: boolean) => {
    if (!updatePreferences) return
    try {
      setSavingPreference(true)
      await updatePreferences({ [key]: value })
    } catch (prefError) {
      console.error('[Storybooks] Failed to update preference', prefError)
      alert('Unable to update preference right now. Please try again later.')
    } finally {
      setSavingPreference(false)
    }
  }

  const handleDeleteStorybook = useCallback(
    async (bookId: string) => {
      if (!bookId || !teacherId) return
      const confirmed = window.confirm(
        'Delete this draft? This action cannot be undone and removes any generated pages.'
      )
      if (!confirmed) return

      setDeletingId(bookId)
      try {
        const { error } = await supabase
          .from('story_books')
          .delete()
          .eq('id', bookId)
          .eq('teacher_id', teacherId)

        if (error) throw error

        setStorybooks((prev) => (Array.isArray(prev) ? prev.filter((book) => book.id !== bookId) : []))
      } catch (deleteError) {
        console.error('[Storybooks] Failed to delete storybook', deleteError)
        alert('We could not delete that storybook right now. Please try again later.')
      } finally {
        setDeletingId(null)
      }
    },
    [teacherId],
  )

  if (!enabled) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold text-cocoa">Storybooks</h2>
        <p className="rounded-2xl bg-shell p-6 text-sm text-cocoa/70">
          Storybooks are not enabled for this deployment. Set <code>VITE_STORYBOOKS_ENABLED=true</code> and redeploy when you’re
          ready.
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold text-cocoa">Storybooks</h2>
        <p className="text-sm text-cocoa/70">Loading your classroom Storybooks…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold text-cocoa">Storybooks</h2>
        <p className="rounded-2xl bg-rose-100 p-4 text-sm text-rose-700">
          We couldn’t load Storybooks right now. Please refresh or try again later.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-10">
      <header className="space-y-2">
        <p className="text-sm uppercase tracking-wide text-cocoa/60">Teacher Story Studio</p>
        <h1 className="text-3xl font-semibold text-cocoa">📖 Storybooks</h1>
        <p className="text-cocoa/70">Create magical class storybooks from your class photos. Everything stays private until you choose to share.</p>
      </header>

      <div className="flex flex-col gap-3 rounded-3xl border border-sand bg-white p-6 shadow-soft md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-baseline gap-4">
            <div>
              <p className="text-sm text-cocoa/70">Tower Credits</p>
              <p className="text-2xl font-semibold text-cocoa">{creditsRemaining} TC</p>
            </div>
            <div className="border-l border-sand pl-4">
              <p className="text-sm text-cocoa/70">Next storybook</p>
              <p className="text-2xl font-semibold text-cocoa">
                {nextStorybookCost === 0 ? (
                  <span className="text-emerald-600">Free</span>
                ) : (
                  <span>{nextStorybookCost} TC</span>
                )}
              </p>
            </div>
            {hasFreeAvailable && (
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
                1 free this week
              </span>
            )}
          </div>
          {helperMessage && <p className="mt-2 max-w-xl text-sm text-cocoa/70">{helperMessage}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="btn"
            onClick={handleNewStorybook}
            disabled={!canCreate || creating}
          >
            {creating ? 'Starting…' : 'New Storybook'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('settings')}
          >
            Manage settings
          </button>
        </div>
      </div>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-cocoa">Your storybooks</h2>
          <button
            type="button"
            className="text-sm font-medium text-cocoa/70 underline-offset-4 hover:underline"
            onClick={handleNewStorybook}
            disabled={!canCreate || creating}
          >
            {creating ? 'Starting…' : 'Start a new draft'}
          </button>
        </div>

        {listError && (
          <p className="rounded-2xl bg-rose-100 p-4 text-sm text-rose-700">We couldn’t load your storybooks right now.</p>
        )}

        {listLoading ? (
          <p className="text-sm text-cocoa/70">Loading storybooks…</p>
        ) : storybooks.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-sand bg-shell/60 p-8 text-center text-cocoa/70">
            No storybooks yet. Start one with your favorite class moments.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {storybooks.map((book) => {
              const isDraft = book.status === 'draft'
              const isDeleting = deletingId === book.id
              const updatedAt = new Date(book.updated_at || book.created_at || Date.now()).toLocaleString()

              return (
                <div
                  key={book.id}
                  className="group flex flex-col gap-4 rounded-3xl border border-sand bg-white p-5 text-left shadow-soft transition hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <button
                        type="button"
                        className="text-left"
                        onClick={() => navigate(book.id)}
                      >
                        <h3 className="text-lg font-semibold text-cocoa group-hover:text-brick">{book.title}</h3>
                        <p className="text-sm text-cocoa/70">Updated {updatedAt}</p>
                      </button>
                      <div className="flex flex-wrap gap-2 text-xs text-cocoa/60">
                        {book.include_illustrations && <span className="rounded-full bg-shell px-3 py-1">Illustrations</span>}
                        {book.pdf_url && <span className="rounded-full bg-shell px-3 py-1">PDF ready</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <StatusBadge status={(book.status || 'draft') as StorybookStatus} />
                      {isDraft && (
                        <button
                          type="button"
                          onClick={() => handleDeleteStorybook(book.id)}
                          disabled={isDeleting}
                          className="text-xs font-medium text-rose-600 underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isDeleting ? 'Deleting…' : 'Delete draft'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button type="button" className="btn-secondary" onClick={() => navigate(book.id)}>
                      Open storybook
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-cocoa">New Storybook</h3>
                <p className="text-sm text-cocoa/70">Select your class cast, pick a style, and add notes for the AI.</p>
              </div>
              <button
                type="button"
                className="rounded-full border border-sand bg-shell px-3 py-1 text-sm text-cocoa/70 hover:text-cocoa"
                onClick={() => !creating && setShowCreateModal(false)}
                disabled={creating}
              >
                Close
              </button>
            </div>

            {modalError && <p className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">{modalError}</p>}

            <div className="mt-4 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-cocoa/60">Story title</label>
                  <input
                    className="input mt-1 w-full"
                    placeholder="Optional; AI will generate one if empty"
                    value={form.title}
                    onChange={(event) => updateField('title', event.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-cocoa/60">Style</label>
                  <select
                    className="input mt-1 w-full"
                    value={form.styleId}
                    onChange={(event) => updateField('styleId', event.target.value)}
                  >
                    {STYLE_OPTIONS.map((style) => (
                      <option key={style.id} value={style.id}>
                        {style.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-cocoa/60">Teacher notes</label>
                <textarea
                  className="input mt-1 min-h-[120px] w-full resize-y"
                  placeholder="What should this story focus on? Themes, events, or emotions welcome."
                  value={form.notes}
                  onChange={(event) => updateField('notes', event.target.value)}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-cocoa">Classroom cast</p>
                    <p className="text-xs text-cocoa/70">Pick the students who should appear in this book.</p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-cocoa/80">
                    <input
                      type="checkbox"
                      className="rounded border-sand text-brick focus:ring-brick"
                      checked={form.includeTeacher}
                      onChange={(event) => updateField('includeTeacher', event.target.checked)}
                    />
                    Include me
                  </label>
                </div>
                <div className="max-h-48 space-y-2 overflow-auto rounded-2xl border border-sand/80 p-3">
                  {rosterLoading && <p className="text-sm text-cocoa/60">Loading students…</p>}
                  {!rosterLoading && roster.length === 0 && (
                    <p className="text-sm text-cocoa/60">No students found yet. Add students to this classroom first.</p>
                  )}
                  {roster.map((student) => {
                    const checked = form.participantIds.includes(student.id)
                    return (
                      <label
                        key={student.id}
                        className="flex cursor-pointer items-center justify-between rounded-xl px-3 py-2 hover:bg-shell/60"
                      >
                        <span className="text-sm text-cocoa">{student.display_name || 'Student'}</span>
                        <input
                          type="checkbox"
                          className="rounded border-sand text-brick focus:ring-brick"
                          checked={checked}
                          onChange={() => toggleStudent(student.id)}
                        />
                      </label>
                    )
                  })}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="btn"
                  onClick={submitModal}
                  disabled={creating || !form.styleId}
                >
                  {creating ? 'Starting…' : 'Create & generate'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => !creating && setShowCreateModal(false)}
                  disabled={creating}
                >
                  Cancel
                </button>
                <p className="text-xs text-cocoa/60">A new Storybook will lock to this classroom and AI pipeline.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-cocoa">Niche Classroom Apps</h2>
          <span className="rounded-full bg-shell px-3 py-1 text-xs font-medium text-cocoa/70">Coming soon</span>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {NICHE_APPS.map((app) => {
            const notifyKey = `notify_${app.key}`
            const notified = Boolean((preferences as Record<string, unknown>)?.[notifyKey])
            return (
              <div key={app.key} className="flex flex-col gap-3 rounded-3xl border border-sand bg-white p-5 shadow-soft">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-cocoa">{app.title}</h3>
                  <span className="rounded-full bg-cocoa/10 px-3 py-1 text-xs font-semibold text-cocoa/80">Coming soon</span>
                </div>
                <p className="text-sm text-cocoa/70">{app.description}</p>
                <div className="mt-auto flex items-center justify-between">
                  <button type="button" className="text-sm font-medium text-brick underline-offset-4 hover:underline" onClick={() => setActiveModal(app)}>
                    Learn more
                  </button>
                  <label className="inline-flex items-center gap-2 text-xs text-cocoa/70">
                    <input
                      type="checkbox"
                      className="rounded border-sand text-brick focus:ring-brick"
                      checked={notified}
                      onChange={(event) => handlePreferenceToggle(notifyKey, event.target.checked)}
                      disabled={savingPreference}
                    />
                    Notify me
                  </label>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <Modal
        open={Boolean(activeModal)}
        onClose={() => setActiveModal(null)}
        title={activeModal?.title || 'Coming soon'}
      >
        <p>{activeModal?.description}</p>
        <p>
          We’ll email you as soon as <strong>{activeModal?.title}</strong> opens for early access. Use the “Notify me” toggle on the
          card to opt in or out at any time.
        </p>
      </Modal>
    </div>
  )
}
